import { NavService } from 'src/app/core/services/nav.service';
import { Component, HostListener, Input } from '@angular/core';
import { MappingService } from '../mapping.service';
import { SupplierId, TypeId, VendorItem } from 'src/app/Models/vendorItem.model';
import { MasterItem } from 'src/app/Models/masterItem.model';
import { firstValueFrom, Subject, observable, takeUntil, switchMap, debounceTime } from 'rxjs';
import { RelatedProductsMappingItem } from 'src/app/Models/relatedProductsMappingItem.model';
import { ModalDismissReasons, NgbModal, NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
import { PhotoCarouselComponent } from 'src/app/mapping/photo-carousel/photo-carousel.component';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'app-mapping-form',
  templateUrl: './mapping-form.component.html',
  styleUrls: ['./mapping-form.component.scss']
})
export class MappingFormComponent {
  [x: string]: any;
  TypeId = TypeId;
  vendorItemList: VendorItem[];
  masterItemList: MasterItem[];
  vendorIdList: { name: string; vendorId: number }[] = [];
  taxonomyList = Object.values(TypeId).filter(value => typeof value === 'string');
  selectedVendor: number | undefined = undefined;
  selectedTaxonomy: TypeId;
  selectedVendorItem: VendorItem | null = null;
  selectedMasterItem: MasterItem | null = null;
  relatedProducts: RelatedProductsMappingItem[];
  isUnmappedOnly: boolean = true;
  isGeneralBrandOnly: boolean = false;
  isBrandSpecificOnly: boolean = false;
  vendorSearchTerm: string = '';
  masterSearchTerm: string = '';
  availableBrands: MasterItem[] = [];
  selectedBrand: MasterItem | null = null;
  private readonly LAST_SELECTED_BRAND_KEY = 'mapping.lastSelectedBrand';
  private brandSpecificMappingKeys = new Set<string>();
  private brandSpecificMappingIds = new Map<string, number>();
  activeBrandPickerVendorItemId: number | null = null;
  isLoading: boolean = false;
  // Cached filtered lists — recomputed only when source list or search term changes,
  // so cdk-virtual-scroll doesn't re-diff on every change-detection cycle.
  filteredVendorItemList: VendorItem[] = [];
  filteredMasterItemList: MasterItem[] = [];
  // Master list virtual-scroll item height — must match itemSize on the viewport
  // in the template AND .virtual-list--master .list-item height in SCSS, otherwise
  // CDK's translateY math drifts from the rendered row height and rows skip on scroll.
  readonly masterItemSize = 48;
  // Matches the previous .card max-height: calc(100vh - 22rem) — used as the cap
  // so short lists collapse to content height instead of leaving empty space.
  private readonly listHeightOffsetPx = 22 * 16;
  private readonly listViewportPaddingPx = 12;
  private onDestroy$ = new Subject<void>();
  private vendorListTrigger$ = new Subject<void>();
  private vendorSearch$ = new Subject<string>();
  private masterSearch$ = new Subject<string>();
  constructor(
    private navServices: NavService,
    private mappingService: MappingService,
    private router: Router,
    private route: ActivatedRoute,
    config: NgbModalConfig,
    private modalService: NgbModal
  ) {
    console.log('MappingFormComponent instantiated');
    config.backdrop = 'static';
    config.keyboard = false;
  }

  ngOnInit(): void {
    this.navServices.collapseSidebar = true;
    this.loadOrganizations();
    this.loadBrands();
    this.setupVendorListStream();
    this.setupSearchStreams();
    this.applyQueryParams();
  }

  private setupSearchStreams(): void {
    this.vendorSearch$
      .pipe(debounceTime(150), takeUntil(this.onDestroy$))
      .subscribe(() => this.recomputeFilteredVendorList());
    this.masterSearch$
      .pipe(debounceTime(150), takeUntil(this.onDestroy$))
      .subscribe(() => this.recomputeFilteredMasterList());
  }

  public onVendorSearchChange(value: string): void {
    this.vendorSearchTerm = value;
    this.vendorSearch$.next(value);
  }

  public onMasterSearchChange(value: string): void {
    this.masterSearchTerm = value;
    this.masterSearch$.next(value);
  }

  private recomputeFilteredVendorList(): void {
    const list = this.vendorItemList || [];
    const term = (this.vendorSearchTerm || '').toLowerCase().trim();
    if (!term) {
      this.filteredVendorItemList = list;
      return;
    }
    this.filteredVendorItemList = list.filter(item => {
      const name = (item?.idName || '').toLowerCase();
      const value = (item?.systemItemValue || '').toString().toLowerCase();
      return name.includes(term) || value.includes(term);
    });
  }

  private recomputeFilteredMasterList(): void {
    const list = this.masterItemList || [];
    const term = (this.masterSearchTerm || '').toLowerCase().trim();
    if (!term) {
      this.filteredMasterItemList = list;
      return;
    }
    this.filteredMasterItemList = list.filter(item => (item?.name || '').toLowerCase().includes(term));
  }

  public get masterViewportHeight(): number {
    return this.computeViewportHeight(this.filteredMasterItemList?.length || 0, this.masterItemSize);
  }

  private computeViewportHeight(count: number, itemSize: number): number {
    const viewportH = typeof window !== 'undefined' ? window.innerHeight : 800;
    const maxPx = Math.max(itemSize * 2, viewportH - this.listHeightOffsetPx);
    const contentPx = count * itemSize + this.listViewportPaddingPx;
    return Math.min(maxPx, Math.max(itemSize, contentPx));
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    // Triggers recompute of masterViewportHeight via change detection.
  }

  private setupVendorListStream(): void {
    this.vendorListTrigger$
      .pipe(
        debounceTime(200),
        switchMap(() => {
          const taxonomy = this.getSelectedTaxonomyValue();
          this.isLoading = true;
          return this.mappingService.getVendorList(this.selectedVendor!, taxonomy as TypeId, this.getListFilters());
        }),
        takeUntil(this.onDestroy$)
      )
      .subscribe({
        next: (data) => {
          this.vendorItemList = data;
          this.recomputeFilteredVendorList();
          this.loadBrandSpecificMappingKeys();
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }

  public trackByVendorItem(_: number, item: VendorItem): number {
    return item.id;
  }

  public trackByMasterItem(_: number, item: MasterItem): number {
    return item.id;
  }

  public clearVendorSearch(): void {
    this.vendorSearchTerm = '';
    this.recomputeFilteredVendorList();
  }

  public clearMasterSearch(): void {
    this.masterSearchTerm = '';
    this.recomputeFilteredMasterList();
  }

  private applyQueryParams(): void {
    this.route.queryParamMap.pipe(takeUntil(this.onDestroy$)).subscribe(params => {
      const vendorId = Number(params.get('vendorId'));
      const brandId = Number(params.get('brandId'));
      const taxonomyParam = params.get('taxonomy');

      if (vendorId) {
        this.selectedVendor = vendorId;
      }

      if (taxonomyParam) {
        const taxonomyNumeric = Number(taxonomyParam);
        if (taxonomyNumeric && TypeId[taxonomyNumeric]) {
          this.selectedTaxonomy = TypeId[taxonomyNumeric] as unknown as TypeId;
        } else if (TypeId[taxonomyParam as keyof typeof TypeId]) {
          this.selectedTaxonomy = taxonomyParam as unknown as TypeId;
        }
      }

      if (brandId && this.availableBrands?.length) {
        this.selectedBrand = this.availableBrands.find(item => item.id === brandId) || this.selectedBrand;
      }

      if (this.selectedVendor !== undefined && this.selectedTaxonomy !== undefined) {
        this.getList();
      }
    });
  }

  private loadOrganizations(): void {
    this.mappingService.getOrganizations(2).pipe(takeUntil(this.onDestroy$)).subscribe(
      (data) => {
        this.vendorIdList = data.map(org => ({
          name: org.name,
          vendorId: org.vendorId
        }));
      },
      (error) => {
        console.error('Error fetching organizations:', error);
      }
    );
  }

  public selectVendor(vendor: { name: string; vendorId: number }): void {
    this.selectedVendor = vendor.vendorId;
    this.getList();
  }

  public getSelectedVendorName(): string {
    if (this.selectedVendor === undefined) {
      return 'Select Vendor';
    }
    const vendor = this.vendorIdList.find(v => v.vendorId === this.selectedVendor);
    return vendor?.name || 'Select Vendor';
  }

  public selectTaxonomy(taxonomy: string | TypeId) {
    this.selectedTaxonomy = taxonomy as TypeId;
    this.getList();
  }

  private loadBrands(): void {
    this.mappingService.getMasterList(TypeId.Brands as unknown as TypeId).pipe(takeUntil(this.onDestroy$)).subscribe({
      next: (brands) => {
        this.availableBrands = brands || [];
        this.restoreLastSelectedBrand();
        const brandIdFromQuery = Number(this.route.snapshot.queryParamMap.get('brandId'));
        if (brandIdFromQuery) {
          const brand = this.availableBrands.find(item => item.id === brandIdFromQuery) || null;
          if (brand) {
            this.selectedBrand = brand;
          }
        }
      },
      error: (error) => {
        console.error('Error loading brands for mapping:', error);
      }
    });
  }

  private restoreLastSelectedBrand(): void {
    const savedBrandId = Number(localStorage.getItem(this.LAST_SELECTED_BRAND_KEY));
    if (!savedBrandId) {
      return;
    }
    const brand = this.availableBrands.find(item => item.id === savedBrandId) || null;
    if (brand) {
      this.selectedBrand = brand;
    }
  }

  public selectBrand(brand: MasterItem): void {
    this.selectedBrand = brand;
    localStorage.setItem(this.LAST_SELECTED_BRAND_KEY, String(brand.id));
    this.loadBrandSpecificMappingKeys();
  }

  public getSelectedBrandName(): string {
    return this.selectedBrand?.name || 'Select Brand';
  }

  public updateProducts(): void {
    if (this.selectedVendor !== undefined && this.selectedTaxonomy !== undefined) {
      this.mappingService.updateProducts(this.selectedVendor, this.selectedTaxonomy);
    } else {
      console.error('Selected vendor or taxonomy is undefined.');
    }
  }


  public selectMasterItem(masterItem: MasterItem): void {
    if (this.selectedMasterItem === masterItem) {
      this.selectedMasterItem = null;
    } else {
      this.selectedMasterItem = masterItem;
    }
    if (this.selectedVendorItem?.id !== undefined && this.selectedMasterItem?.id !== undefined) {
      // For size mappings always pass brandId=0 + sizeTypeId to keep endpoint shape consistent.
      const sizeTypeId = this.isSizeMapping() ? this.selectedVendorItem?.sizeTypeId : undefined;
      const brandId = this.isSizeMapping() ? 0 : undefined;
      this.updateSystemItemId(this.selectedVendorItem?.id, this.selectedMasterItem?.id, brandId, sizeTypeId);
      const selectedVendorItemIndex = this.vendorItemList.findIndex(item => item.id === this.selectedVendorItem?.id);
      this.vendorItemList[selectedVendorItemIndex].systemItemValue = this.selectedMasterItem.name;
    }
  }

  public selectVendorItem(vendorItem: VendorItem): void {
    if (this.selectedVendorItem === vendorItem) {
      this.selectedVendorItem = null;
    } else {
      this.selectedVendorItem = vendorItem;
    }
    if (this.selectedMasterItem?.id !== undefined && this.selectedVendorItem?.id !== undefined) {
      // For size mappings always pass brandId=0 + sizeTypeId to keep endpoint shape consistent.
      const sizeTypeId = this.isSizeMapping() ? this.selectedVendorItem?.sizeTypeId : undefined;
      const brandId = this.isSizeMapping() ? 0 : undefined;
      this.updateSystemItemId(this.selectedVendorItem?.id, this.selectedMasterItem?.id, brandId, sizeTypeId);
      const selectedVendorItemIndex = this.vendorItemList.findIndex(item => item.id === vendorItem.id);
      this.vendorItemList[selectedVendorItemIndex].systemItemValue = this.selectedMasterItem.name;
    }
  }


  public removeVendorItemMapping(vendorItem: VendorItem): void {
    if (vendorItem?.id !== undefined) {
      this.mappingService.deleteMapping(vendorItem.id).pipe(takeUntil(this.onDestroy$)).subscribe({
        next: () => {
          const selectedVendorItemIndex = this.vendorItemList.findIndex(item => item.id === vendorItem.id);
          if (selectedVendorItemIndex !== -1) {
            this.vendorItemList[selectedVendorItemIndex].systemItemValue = null;
            this.vendorItemList[selectedVendorItemIndex].systemItemId = 0;
          }
          this.selectedVendorItem = null;
          this.selectedMasterItem = null;
        },
        error: (error) => {
          console.error('Failed to delete mapping', error);
        }
      });
    }
  }

  public async createBrandSpecificMapping(vendorItem: VendorItem, brand?: MasterItem): Promise<void> {
    const selectedBrand = brand || this.selectedBrand;
    if (!selectedBrand) {
      alert('Please select a brand first.');
      return;
    }
    if (!vendorItem?.id || !vendorItem.systemItemId) {
      alert('Only mapped rows can be used for brand-specific mapping.');
      return;
    }

    const duplicateKey = this.getBrandSpecificKey(vendorItem.id, selectedBrand.id);
    if (this.brandSpecificMappingKeys.has(duplicateKey)) {
      alert('Brand-specific mapping already exists for this row and brand.');
      return;
    }

    try {
      // Brand-specific mapping from row brand selection uses dedicated endpoint.
      const result = await firstValueFrom(
        this.mappingService.createBrandSpecificMapping(vendorItem.id, selectedBrand.id)
      );
      if (result) {
        this.brandSpecificMappingKeys.add(duplicateKey);
        const createdId = this.extractResponseId(result);
        if (createdId) {
          this.brandSpecificMappingIds.set(duplicateKey, createdId);
        }
      } else {
        alert('Brand-specific mapping already exists or could not be created.');
      }
    } catch (error) {
      console.error('Failed to create brand-specific mapping', error);
    }
  }

  public removeBrandSpecificMapping(vendorItem: VendorItem): void {
    if (!this.selectedBrand) {
      alert('Please select a brand first.');
      return;
    }
    if (!vendorItem?.id) {
      return;
    }
    const duplicateKey = this.getBrandSpecificKey(vendorItem.id, this.selectedBrand.id);
    const brandMappingId = this.brandSpecificMappingIds.get(duplicateKey) || vendorItem.id;
    this.mappingService.deleteMapping(brandMappingId)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: () => {
          // DELETE can return empty body on success; clear UI state on successful response.
          this.brandSpecificMappingKeys.delete(duplicateKey);
          this.brandSpecificMappingIds.delete(duplicateKey);
        },
        error: (error) => {
          console.error('Failed to delete brand-specific mapping', error);
        }
      });
  }

  private getBrandSpecificKey(vendorItemId: number, brandId: number): string {
    return `${vendorItemId}-${brandId}-${this.selectedVendor ?? 0}-${this.selectedTaxonomy ?? ''}`;
  }


  public updateSystemItemId(
    id: number,
    systemItemId: number,
    brandId?: number,
    sizeTypeId?: number,
    onSuccess?: (result: any) => void
  ): void {
    if (id !== undefined && systemItemId !== undefined) {
      // Validate that sizeTypeId is only used for size mappings (TypeId = 2)
      if (sizeTypeId !== undefined && this.isSizeMapping() === false) {
        console.error('sizeTypeId can only be used for size mappings (TypeId = 2)');
        return;
      }

      this.mappingService.updateSystemItemId(id, systemItemId, brandId, sizeTypeId).pipe(takeUntil(this.onDestroy$)).subscribe(result => {
        if (result) {
          const selectedVendorItemIndex = this.vendorItemList.findIndex(item => item.id === id);

          if (selectedVendorItemIndex !== -1) {
            if (systemItemId !== undefined) {
              this.vendorItemList[selectedVendorItemIndex].systemItemId = systemItemId;
            }
          }

          // Clear selected items
          this.selectedVendorItem = null;
          this.selectedMasterItem = null;
          if (onSuccess) {
            onSuccess(result);
          }
        } else {

          // Handle failure or error
          console.error('Failed to update system item ID.');
        }
      },
        error => {
          // Handle API error responses
          console.error('Error updating system item ID:', error);
          if (error.error && error.error.message) {
            console.error('API Error:', error.error.message);
          }
        });
    }
  }

  public navigateToBrandMappingManagement(): void {
    this.router.navigate(['/main/products/mapping/brand-management'], {
      queryParams: {
        vendorId: this.selectedVendor,
        brandId: this.selectedBrand?.id || null
      }
    });
  }


  public getList() {
    if (this.selectedVendor !== undefined && this.selectedTaxonomy) {

      const taxonomy = this.getSelectedTaxonomyValue();
      if (taxonomy === undefined) {
        console.error('Unable to resolve selected taxonomy value.', this.selectedTaxonomy);
        return;
      }
      this.mappingService.getMasterList(taxonomy).pipe(takeUntil(this.onDestroy$)).subscribe((data) => {
        this.masterItemList = data;
        this.recomputeFilteredMasterList();
      });

      this.vendorListTrigger$.next();
    }
  }

  private getListFilters(): { isMapped?: boolean; isGeneralBrand?: boolean } {
    const filters: { isMapped?: boolean; isGeneralBrand?: boolean } = {};
    filters.isMapped = !this.isUnmappedOnly;
    if (this.isSizeMapping()) {
      if (this.isGeneralBrandOnly && !this.isBrandSpecificOnly) {
        filters.isGeneralBrand = true;
      } else if (this.isBrandSpecificOnly && !this.isGeneralBrandOnly) {
        filters.isGeneralBrand = false;
      }
    }
    return filters;
  }

  public onGeneralBrandToggle(checked: boolean): void {
    this.isGeneralBrandOnly = checked;
    if (checked) {
      this.isBrandSpecificOnly = false;
    }
    this.getList();
  }

  public onBrandSpecificToggle(checked: boolean): void {
    this.isBrandSpecificOnly = checked;
    if (checked) {
      this.isGeneralBrandOnly = false;
    }
    this.getList();
  }

  public onUnmappedToggle(): void {
    this.getList();
  }

  private loadBrandSpecificMappingKeys(): void {
    this.brandSpecificMappingKeys.clear();
    this.brandSpecificMappingIds.clear();
  }

  public isBrandSpecificMapped(vendorItem: VendorItem): boolean {
    if (!this.selectedBrand || !vendorItem?.id) {
      return false;
    }
    return this.brandSpecificMappingKeys.has(this.getBrandSpecificKey(vendorItem.id, this.selectedBrand.id));
  }

  public getMappedBrandName(vendorItem: VendorItem): string {
    if (!this.selectedBrand || !this.isBrandSpecificMapped(vendorItem)) {
      return '';
    }
    return this.selectedBrand.name;
  }

  private extractResponseId(result: any): number | undefined {
    if (typeof result === 'number') {
      return result;
    }
    if (result && typeof result.id === 'number') {
      return result.id;
    }
    if (result && typeof result.brandSpecificMappingId === 'number') {
      return result.brandSpecificMappingId;
    }
    if (result && result.data && typeof result.data.id === 'number') {
      return result.data.id;
    }
    return undefined;
  }

  public openBrandPicker(vendorItem: VendorItem): void {
    this.activeBrandPickerVendorItemId = vendorItem?.id || null;
  }

  public createBrandSpecificForSelectedBrand(vendorItem: VendorItem, brand: MasterItem): void {
    this.selectedBrand = brand;
    localStorage.setItem(this.LAST_SELECTED_BRAND_KEY, String(brand.id));
    this.createBrandSpecificMapping(vendorItem, brand);
  }


  /**
   * Check if the current taxonomy is for size mappings (TypeId = 2)
   * sizeTypeId should only be used for size mappings
   */
  public isSizeMapping(): boolean {
    const taxonomyValue = this.getSelectedTaxonomyValue();
    return taxonomyValue === TypeId.Sizes;
  }

  private getSelectedTaxonomyValue(): number | undefined {
    if (this.selectedTaxonomy === undefined || this.selectedTaxonomy === null) {
      return undefined;
    }

    // Handles numeric enum value (e.g. 2)
    if (typeof this.selectedTaxonomy === 'number') {
      return this.selectedTaxonomy;
    }

    // Handles enum key string (e.g. "Sizes")
    const enumValue = TypeId[this.selectedTaxonomy as keyof typeof TypeId];
    if (typeof enumValue === 'number') {
      return enumValue;
    }

    // Handles numeric string (e.g. "2")
    const numeric = Number(this.selectedTaxonomy);
    return Number.isNaN(numeric) ? undefined : numeric;
  }

  content: string;

  async openVerticallyCentered(content, vendorItem: VendorItem) {

    console.log("this.selectedVendorItem", vendorItem);
    var sizeTypeId = vendorItem?.sizeTypeId || undefined;


    try {
      this.relatedProducts = await this.mappingService.getRelatedProducts(vendorItem.id, this.selectedTaxonomy, sizeTypeId);
      console.log(this.relatedProducts);
    } catch (error) {
      console.error('Error fetching related products:', error);
    }
    this.modalService.open(content, { centered: true });
  }



  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
